import { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

// Components
import NavLink from 'components/v1/SideNavigation/NavLink';
import NavSection from 'components/v1/SideNavigation/NavSection';
import NavWrapper from 'components/v1/SideNavigation/NavWrapper';

import { iconComponents } from 'lib/v2/icons';

// paths
import peoplePaths from 'config/paths/people';
import reportablesPaths from 'config/paths/reportablesV2';
import reportsPaths from 'config/paths/reports';
import ticketPaths from 'config/paths/tickets';
import trainingPaths from 'config/paths/training';
import travelPaths from 'config/paths/travels';
import workflowControlsPaths from 'config/paths/workflowControls';
import visitRequestsPaths from 'config/paths/visitRequests';

// mappers
import { mapPermissionsToProps } from 'mappers/v1/permissions';
import { mapPreferencesToProps } from 'mappers/v1/preferences';

// Utils
import { canSeeAnalyticsSidebar } from 'lib/v1/looker';

export const SideNavigation = ({ profile, permissions, preferences }) => {
  const [show, setShow] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (show && ref.current && !ref.current.contains(event.target)) {
        setShow(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [show]);

  const renderAnalytics = () => {
    return (
      <NavLink
        to={reportsPaths.root}
        icon={iconComponents('analytics')}
        updated={true}
        label="Analytics"
        allowed={canSeeAnalyticsSidebar(profile)}
        featureCallout={profile.role === 'admin'}
      />
    );
  };

  const renderAdmin = () => {
    if (permissions.admin.list) {
      return <NavLink to="/admin" icon={iconComponents('admin')} label="Administration" />;
    }
  };

  const hasNotifyPermissions =
    permissions.reportable.list ||
    permissions.reportable.create ||
    permissions.visits.list ||
    permissions.travels.list ||
    permissions.actionRequests.list;

  const sections = [
    {
      icon: iconComponents('inbox'),
      label: 'Inbox',
      to: '/inbox',
      allowed: true,
    },
    {
      icon: iconComponents('people'),
      label: 'People',
      to: peoplePaths.root,
      allowed: permissions.people.list,
    },
    {
      icon: iconComponents('travel'),
      label: 'Travel',
      to: travelPaths.root,
      allowed: permissions.travels.list,
    },
    {
      icon: iconComponents('visits'),
      label: 'Visits',
      to: visitRequestsPaths.root,
      allowed: permissions.visits.list,
    },
    {
      icon: iconComponents('notify'),
      label: 'Reports',
      to: reportablesPaths.root,
      allowed: hasNotifyPermissions,
    },
    {
      icon: iconComponents('training'),
      label: 'Training',
      to: trainingPaths.root,
      allowed: preferences.trainings,
    },
    {
      icon: iconComponents('tickets'),
      label: 'Tickets',
      to: ticketPaths.root,
      activePaths: [ticketPaths.root],
      allowed: preferences.workflows,
    },
    {
      icon: iconComponents('workflows'),
      label: 'Workflows',
      to: workflowControlsPaths.root,
      activePaths: [workflowControlsPaths.root],
      allowed: permissions.workflows.list,
      featureCallout: profile.role === 'admin',
    },
  ].filter(({ allowed, featureCallout }) => allowed || featureCallout);

  return (
    <NavWrapper ref={ref}>
      <NavSection>
        {sections.map((section, i) => {
          return <NavLink key={i} {...section} />;
        })}
      </NavSection>

      <NavSection>
        {renderAnalytics()}
        {renderAdmin()}
      </NavSection>
    </NavWrapper>
  );
};

function mapStateToProps(store) {
  const { profile } = store;
  return {
    ...mapPreferencesToProps({ profile }),
    profile,
    id: profile.id,
    ...mapPermissionsToProps({ profile }),
  };
}

export default withRouter(connect(mapStateToProps)(SideNavigation));
